import { Node } from 'tree-model'
import { inject, injectable } from 'inversify'
import { NavigationRepository } from './navigation.repository'
import type { NavNode } from './navigation.config'
import { getRoutableParent, containsRoute } from './navigation-tree.helpers'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { action, computed, makeObservable } from 'mobx'
import type { RouteId } from '@core/routing/router.config'
import { UserModel } from '@core/infra/authentication/user.model'
import { AdminModeRepository } from '@core/infra/authentication/admin-mode.repository'
import { Config } from '@core/config'
import { RouterRepository } from '@core/routing/router.repository'
import type { ActiveGamesSource, ActiveGameEntryVm } from '@core/interfaces/active-games-source'
import { Types } from '@core/ioc/types'

export type MenuItem = {
  /**
   * Stable identity for expansion/column state — routeId, else the node's
   * translation key. Never the rendered label: that changes with the locale,
   * and an open column must not collapse because the user switched language.
   */
  key: string
  buttonText: string
  expanded: boolean
  clickable: boolean
  expandable: boolean
  current: boolean
  routesTo?: RouteId
  urlTo?: string
  level: number
  preIcon?: string
  // Resolved rather than `newsCount?: number` (CI-9's out-rule): the badge is
  // a display decision — has one, or doesn't — not a number a reader has to
  // compare against `undefined` to find out.
  showNewsCount: boolean
  newsCount: number
  expand: () => void
  navigate: () => Promise<void>
  children?: MenuItem[]
  // Set only for expandable nodes configured with `columnView` in navigation.config.ts —
  // such nodes open their children in a new sidebar column instead of expanding inline.
  columnView?: boolean
  columnOpen?: boolean
  openColumn?: () => void
  /**
   * Set on the first visible member of a `group` node: the resolved heading to
   * render above this row. Groups are flattened away here, so the list stays a
   * flat sequence of links and every consumer of it — expansion, columns,
   * mobile drill-down — is untouched by grouping.
   *
   * Carried on the member rather than emitted as its own list entry so that a
   * group whose members are all invisible renders nothing at all, heading
   * included, without anyone having to check for that case.
   */
  groupStart?: string
  /** Set on the last visible member: closes the group off with a trailing gap. */
  groupEnd?: boolean
}

@injectable()
export class NavigationPresenter {
  @inject(NavigationRepository)
  private navigationRepository!: NavigationRepository

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  // For requiresCapability filtering only — capabilities are observable, so the menu
  // reacts to login/logout without any wiring here.
  @inject(UserModel)
  private userModel!: UserModel

  // Same reasoning: adminModePm is observable, so the admin group appears and
  // disappears with the switch without any wiring here.
  @inject(AdminModeRepository)
  private adminModeRepository!: AdminModeRepository

  // Config.showDemoSurface is a static value, not observable — the demo
  // group doesn't need to appear/disappear live the way the admin group
  // does, only to be absent for the lifetime of a production build.
  @inject(Config)
  private config!: Config

  @inject(RouterRepository)
  private routerRepository!: RouterRepository

  // A live entry per open game, spliced under the static "Play" row —
  // observable, so the sidebar updates itself as the source's own poll
  // ticks, with no wiring needed here beyond reading it. Interface-typed
  // (CI-3: `core/` never imports a domain feature) — the one implementation
  // lives in `features/game/active-games.repository.ts`, bound against
  // `Types.IActiveGamesSource` at the composition root.
  @inject(Types.IActiveGamesSource)
  private activeGamesSource!: ActiveGamesSource

  constructor() {
    makeObservable(this, {
      viewModel: computed,
      toggleExpand: action,
      toggleColumn: action,
      closeLastColumn: action,
    })
  }

  get viewModel(): {
    currentSelectedVisibleName: string
    currentSelectedUpTarget: { enabled: boolean; target: string | null }
    currentSelectedSectionTarget: { enabled: boolean; target: string | null }
    menuItems: MenuItem[]
    columns: MenuItem[][]
    // buttonText of the item that opened each column — parallel to `columns`.
    // Mobile's drill-down view uses this as the "you are here" heading, since
    // only one column (the deepest) is visible at a time there.
    columnLabels: string[]
    browseUpTarget: { enabled: boolean; target: string | null }
    isBrowsingAwayFromRoute: boolean
    browseUpLabel: string
    backToCurrentLabel: string
    upLabel: string
    sectionLabel: string
    expandLabel: string
    collapseLabel: string
    // Display decisions resolved here rather than in the component
    // (machine-architecture.md §3.4). Below md exactly one nav block is
    // visible: the primary list when no column is open, otherwise the
    // deepest open column — `activeOnMobile` is parallel to `columns`.
    primaryActiveOnMobile: boolean
    columnsActiveOnMobile: boolean[]
    showUpSectionBar: boolean
  } {
    const vm = {
      currentSelectedVisibleName: '',
      currentSelectedUpTarget: { enabled: false, target: null } as { enabled: boolean; target: string | null },
      currentSelectedSectionTarget: { enabled: false, target: null } as { enabled: boolean; target: string | null },
      menuItems: [] as MenuItem[],
      columns: [] as MenuItem[][],
      columnLabels: [] as string[],
      browseUpTarget: { enabled: false, target: null } as { enabled: boolean; target: string | null },
      isBrowsingAwayFromRoute: this.navigationRepository.browseAnchorPm !== null,
      browseUpLabel: this.i18nRepository.t('nav.browseUp'),
      backToCurrentLabel: this.i18nRepository.t('nav.backToCurrent'),
      upLabel: this.i18nRepository.t('nav.up'),
      sectionLabel: this.i18nRepository.t('nav.section'),
      expandLabel: this.i18nRepository.t('nav.expand'),
      collapseLabel: this.i18nRepository.t('nav.collapse'),
      primaryActiveOnMobile: true,
      columnsActiveOnMobile: [] as boolean[],
      showUpSectionBar: false,
    }

    // Two different questions, and conflating them is what broke Up: the menu
    // lists the *browsed* node's children, while everything describing where
    // the user is — and every control that navigates — reads the *route* node.
    const browseNode = this.navigationRepository.currentNode
    const routeNode = this.navigationRepository.routeNode

    if (browseNode) {
      vm.menuItems = this.nestPlayUnderGames(this.withActiveGames(this.getChildren(browseNode, 0)))
      const opened = this.getOpenColumns(vm.menuItems)
      vm.columns = opened.map((c) => c.items)
      vm.columnLabels = opened.map((c) => c.label)

      // getRoutableParent, not getParent: Up navigates, and a group heading is
      // not somewhere to navigate to. Reading the raw parent would label the
      // button with a heading and then do nothing when pressed.
      const parent = routeNode && getRoutableParent(routeNode)
      if (parent) {
        vm.currentSelectedUpTarget = { enabled: true, target: this.i18nRepository.t(parent.model.labelKey) }
      }
      if (routeNode) vm.currentSelectedVisibleName = this.i18nRepository.t(routeNode.model.labelKey)

      const browseTarget = this.navigationRepository.browseUpTarget
      if (browseTarget) {
        vm.browseUpTarget = { enabled: true, target: this.i18nRepository.t(browseTarget.model.labelKey) }
      }

      // Suppressed when it lands on the same place Up already goes to — Section
      // exists to skip levels Up can't, and a redundant second button that does
      // exactly what Up does is confusing, not helpful.
      if (routeNode?.model.sectionTo && routeNode.model.sectionTo !== parent?.model.routesTo) {
        vm.currentSelectedSectionTarget = { enabled: true, target: routeNode.model.sectionTo }
      }

      vm.primaryActiveOnMobile = vm.columns.length === 0
      vm.columnsActiveOnMobile = vm.columns.map((_, index) => index === vm.columns.length - 1)
      vm.showUpSectionBar =
        vm.currentSelectedUpTarget.enabled || vm.currentSelectedSectionTarget.enabled || vm.browseUpTarget.enabled
    }
    return vm
  }

  /**
   * Attaches one synthetic `MenuItem` per open game as the static "Play"
   * row's own `children` — these have no `NavNode` of their own in
   * `navigation.config.ts` (they're data, not tree structure), so they can't
   * go through `getChildren`/`toMenuItem` and are built here instead.
   * `expanded` is forced `true` (never toggled — `expandable` stays `false`,
   * so there is no chevron/collapse control): real nesting, not just a
   * flat row placed after Play, is the whole point — `NavItemComponent`
   * only ever indents (and only ever renders at all) a parent's own
   * `children`, one `depth` deeper, while `expanded` is true
   * (`nav-item.component.tsx`). Clicking Play's own label still navigates
   * to the lobby exactly as before; only its `children`/`expanded` change
   * here. A no-op when "Play" isn't in the list at all (e.g. filtered out)
   * or there are no open games.
   */
  private withActiveGames(items: MenuItem[]): MenuItem[] {
    const entries = this.activeGamesSource.entries
    if (entries.length === 0) return items

    const playIndex = items.findIndex((item) => item.routesTo === 'Lobby')
    if (playIndex === -1) return items

    const play = items[playIndex]
    const gameItems = entries.map((entry) => this.toActiveGameMenuItem(entry, play.level + 1))
    const nestedPlay: MenuItem = { ...play, expanded: true, children: gameItems }
    return [...items.slice(0, playIndex), nestedPlay, ...items.slice(playIndex + 1)]
  }

  /**
   * Nests the static "Play" row (plus whatever `withActiveGames` has already
   * attached to it) one level under the "Games" row — same forced-`expanded`,
   * no-chevron technique as `withActiveGames` just above, for the same
   * reason: Games is the landing page, Play/The Duke is the one game on offer
   * under it, and there is no toggle for the user to have missed. Run this
   * *after* `withActiveGames` so Play's own active-game children are already
   * in place and get their `level` bumped along with it. A no-op when either
   * row is missing (a fixture harness with no such tree, a filtered-out row).
   */
  private nestPlayUnderGames(items: MenuItem[]): MenuItem[] {
    const playIndex = items.findIndex((item) => item.routesTo === 'Lobby')
    const gamesIndex = items.findIndex((item) => item.routesTo === 'Home')
    if (playIndex === -1 || gamesIndex === -1) return items

    const play = this.bumpLevel(items[playIndex])
    const withoutPlay = items.filter((_, index) => index !== playIndex)
    const newGamesIndex = withoutPlay.findIndex((item) => item.routesTo === 'Home')
    const games = withoutPlay[newGamesIndex]
    const nestedGames: MenuItem = { ...games, expanded: true, children: [play] }
    return [...withoutPlay.slice(0, newGamesIndex), nestedGames, ...withoutPlay.slice(newGamesIndex + 1)]
  }

  private bumpLevel(item: MenuItem): MenuItem {
    return {
      ...item,
      level: item.level + 1,
      children: item.children?.map((child) => this.bumpLevel(child)),
    }
  }

  private toActiveGameMenuItem(entry: ActiveGameEntryVm, level: number): MenuItem {
    const current =
      this.routerRepository.currentRoute.routeId === entry.routesTo &&
      Object.entries(entry.params).every(([key, value]) => this.routerRepository.currentRoute.params[key] === value)

    return {
      key: entry.key,
      buttonText: entry.label,
      expanded: false,
      clickable: true,
      expandable: false,
      current,
      routesTo: entry.routesTo,
      level,
      showNewsCount: false,
      newsCount: 0,
      expand: () => {},
      navigate: () => this.navigationRepository.follow({ routesTo: entry.routesTo, params: entry.params }),
    }
  }

  private getChildren(node: Node<NavNode>, level: number): MenuItem[] {
    const items: MenuItem[] = []

    for (const child of (node.children || []) as Node<NavNode>[]) {
      if (child.model.visible === false) continue
      if (child.model.requiresCapability && !this.userModel.can(child.model.requiresCapability)) continue
      if (child.model.requiresAdminMode && !this.adminModeRepository.adminModePm) continue
      if (child.model.requiresDemoSurface && !this.config.showDemoSurface) continue
      if (child.model.group) {
        // Same `level`: a heading is decoration, not a level of nesting, so its
        // members are siblings of the group itself. That also keeps columnPath
        // indexing (which is keyed by level) correct for a columnView node that
        // happens to sit inside a group.
        const members = this.getChildren(child, level)
        if (members.length === 0) continue
        members[0] = { ...members[0], groupStart: this.i18nRepository.t(child.model.labelKey) }
        members[members.length - 1] = { ...members[members.length - 1], groupEnd: true }
        items.push(...members)
        continue
      }

      items.push(this.toMenuItem(child, level))
    }

    return items
  }

  private toMenuItem(child: Node<NavNode>, level: number): MenuItem {
    const clickable = child.model.clickable ?? false
    const expandable = child.model.expandable ?? false
    const columnView = child.model.columnView ?? false
    // labelKey, not the resolved label: an expansion must survive a locale
    // switch, so the identity cannot be the translated text.
    const expandKey = child.model.routesTo ?? child.model.labelKey

    return {
      key: expandKey,
      buttonText: this.i18nRepository.t(child.model.labelKey),
      expanded: this.navigationRepository.expandedNodesPm.get(expandKey) || false,
      clickable,
      expandable,
      // containsRoute, not an exact routesTo match: a detail route (e.g. Asset)
      // has no menu row of its own, so the row for its parent (Assets) still
      // has to read as "current" while the user is on one of its assets.
      //
      // menuRouteNode, not routeNode: routeNode is ambiguous by construction
      // for the Home route (it also matches the tree's own wrapper root, see
      // navigation-tree.helpers.ts), and that wrapper is never a menu row —
      // comparing against it left Overview's row permanently un-highlighted
      // while on Home.
      current: child.model.routesTo !== undefined && containsRoute(child, this.navigationRepository.menuRouteNode),
      routesTo: child.model.routesTo,
      urlTo: child.model.urlTo,
      level: level,
      preIcon: child.model.preIcon,
      showNewsCount: child.model.newsCount !== undefined,
      newsCount: child.model.newsCount ?? 0,
      expand: () => this.toggleExpand(expandKey),
      navigate: () => this.navigateItem(child),
      // Always populated (not gated on `expanded`) — columnView nodes need their
      // children available for getOpenColumns() without the inline-expand toggle
      // ever firing. `expanded` alone gates inline visibility in the component.
      children: this.getChildren(child, level + 1),
      columnView,
      columnOpen: columnView ? this.navigationRepository.columnPathPm[level] === expandKey : undefined,
      openColumn: columnView ? () => this.toggleColumn(level, expandKey) : undefined,
    }
  }

  // Walks columnPath through the already-built menu tree, collecting one extra
  // column per open columnView node (plus the label of the item that opened
  // it). Self-healing: an unresolvable/mismatched path entry (stale key after
  // data changes) just stops the walk early.
  private getOpenColumns(rootItems: MenuItem[]): { items: MenuItem[]; label: string }[] {
    const columns: { items: MenuItem[]; label: string }[] = []
    let items = rootItems

    const columnPath = this.navigationRepository.columnPathPm

    for (let level = 0; level < columnPath.length; level++) {
      const key = columnPath[level]
      const openItem = items.find((item) => item.key === key)
      if (!openItem?.columnView || !openItem.children) break

      columns.push({ items: openItem.children, label: openItem.buttonText })
      items = openItem.children
    }

    return columns
  }

  toggleExpand(key: string) {
    this.navigationRepository.toggleExpanded(key)
  }

  toggleColumn(level: number, key: string) {
    this.navigationRepository.toggleColumn(level, key)
  }

  // Mobile drill-down "back": closes only the innermost open column, same as
  // tapping its own opener again would (toggleColumn with its own key), but
  // without the component needing to know that key.
  closeLastColumn() {
    this.navigationRepository.closeLastColumn()
  }

  /**
   * Moves the menu one level up the tree without navigating. Distinct from
   * up(), which goes to the parent *route* — browsing changes what is listed,
   * up() changes where the app is.
   */
  browseUp = () => {
    this.navigationRepository.browseUp()
  }

  /**
   * Ends the detour and puts the menu back on the current route.
   *
   * Without it the only way out of browsing was to navigate somewhere — which
   * is precisely what a user who is browsing rather than navigating does not
   * want to do.
   */
  backToCurrent = () => {
    this.navigationRepository.resetBrowseAnchor()
  }

  // Returns nothing: goToId's boolean says whether the navigation was allowed
  // (a leave guard can refuse it), which is the Router's business with the
  // caller that asked — a menu row has nothing to do with the answer.
  private async navigateItem(node: Node<NavNode>): Promise<void> {
    await this.navigationRepository.follow(node.model)
  }

  up() {
    return this.navigationRepository.up()
  }

  section() {
    return this.navigationRepository.section()
  }
}
